export type PollType = "constant_sum_two_level" | "ranking" | "ranking_minimum";

export type PollResultView =
  | "allocation_summary"
  | "stacked_distribution"
  | "histogram"
  | "histogram_v2"
  | "scatter_chart";
export type PollResultsViewConfig = Partial<Record<PollType, PollResultView>>;

export const defaultResultViews: Record<PollType, PollResultView> = {
  constant_sum_two_level: "allocation_summary",
  ranking: "stacked_distribution",
  ranking_minimum: "stacked_distribution",
};

const pollTypeValues: PollType[] = ["constant_sum_two_level", "ranking", "ranking_minimum"];

export const normalizePollType = (value?: string | null): PollType => {
  if (value === "constant_sum") {
    return "constant_sum_two_level";
  }

  if (pollTypeValues.includes(value as PollType)) {
    return value as PollType;
  }

  return "constant_sum_two_level";
};

export const pollResultViewOptions: Record<
  PollType,
  { value: PollResultView; label: string; description: string }[]
> = {
  constant_sum_two_level: [
    {
      value: "allocation_summary",
      label: "Allocation summary",
      description: "Compare your allocation against the global average for each cluster and sub-category.",
    },
    {
      value: "scatter_chart",
      label: "Scatter chart",
      description: "Plot your allocation against the community average on two axes.",
    },
  ],
  ranking: [
    {
      value: "stacked_distribution",
      label: "Rank distribution",
      description: "See how frequently each rank was chosen for every option.",
    },
    {
      value: "histogram",
      label: "Rank histogram",
      description: "View rank choices as vertical bars to compare rank densities.",
    },
  ],
  ranking_minimum: [
    {
      value: "stacked_distribution",
      label: "Rank distribution",
      description: "Stacked bars showing how often each option received each rank.",
    },
    {
      value: "histogram",
      label: "Rank histogram",
      description: "Vertical bars showing the number of responses at each rank (including unranked).",
    },
    {
      value: "histogram_v2",
      label: "Rank histogram v2",
      description:
        "Wide unranked bar with separate low-rank overlays to compare individual low-rank placements.",
    },
    {
      value: "scatter_chart",
      label: "Scatter chart",
      description: "Compare your choices against the community using a two-axis scatter view.",
    },
  ],
};

export interface PollBaseOption {
  id?: string;
  label: string;
  description: string;
  image_url?: string;
  order: number;
}

export interface SubCategory {
  id?: string;
  label: string;
  description: string;
  order: number;
}

export interface ConstantSumCluster extends PollBaseOption {
  subCategories: SubCategory[];
}

export type RankingOption = PollBaseOption;

export interface ConstantSumConfig {
  clusters: ConstantSumCluster[];
  hideZeroAllocated: boolean;
}

export interface RankingConfig {
  options: RankingOption[];
}

export interface RankingMinimumConfig {
  options: RankingOption[];
  minimumRequired: number;
}

export type SavedConfigurations = Partial<
  Record<PollType, ConstantSumConfig | RankingConfig | RankingMinimumConfig>
>;

export interface PollSettings {
  hideZeroAllocated?: boolean;
  minimumRequiredRanks?: number;
  savedConfigurations?: SavedConfigurations;
  resultViews?: PollResultsViewConfig;
}
